import * as CheckoutStepTracker from "@omni/services/tracking/trackers/tools/checkout/checkoutStep.tracker";
import { OrderDetail } from "@omni/types/api/cart/bff/orderDetail.model";
import { DeliveryPromiseType } from "@omni/types/api/product/bff/shared/enums/deliveryPromiseType.enum";
import { DeliveryType } from "@omni/types/api/product/bff/shared/enums/deliveryType.enum";
import { Store } from "@omni/types/api/store/bff/store.model";
import * as OrderUtils from "@omni/utils/order.utils";
import { cn } from "@omni/utils/styles";
import { useEffect } from "react";

import { Row } from "@/components/General/Grid/row.component";
import { TooltipType } from "@/components/General/Tooltip/tooltip.types";
import PriceSummaryComponent from "@/components/Order/shared/PriceSummary/priceSummary.component";
import { DeviceFingerPrintingProvider } from "@/components/Product/Reviews/context/deviceFingerPrinting.context";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { BreakpointProvider } from "@/utils/breakpoints/breakpoints.provider";
import { variableText } from "@/utils/textUtils";

import { ProductCollectionBlock } from "../shared/ProductCollectionBlock/productCollectionBlock.container";
import OrderClickAndCollectDetailsBlock from "./ClickAndCollectDetailsBlock/clickAndCollectDetailsBlock.component";
import OrderCustomerDetailsBlock from "./CustomerDetailsBlock/customerDetailsBlock.component";
import OrderDeliveryDetailsBlock from "./DeliveryDetailsBlock/deliveryDetailsBlock.component";
import { DeliveryPromiseComponent } from "./DeliveryPromise/deliveryPromise.component";
import { OrderDetails } from "./OrderDetails/orderDetails.container";

type Props = {
    orderDetails?: OrderDetail;
    storeDetails?: Store | null;
    innerHTML: any;
    additionalServiceTooltip?: TooltipType;
};

const OrderOverviewComponent = ({
    orderDetails,
    storeDetails,
    innerHTML,
    additionalServiceTooltip
}: Props) => {
    const { t } = useGranite();

    useEffect(() => {
        if (orderDetails) {
            CheckoutStepTracker.trackConfimationSubmit(orderDetails);
        }
    }, [orderDetails]);

    if (!orderDetails) {
        return <div></div>;
    }

    const collectProducts = orderDetails.items.filter(
        (i) =>
            i.deliveryType === DeliveryType.ClickAndCollect &&
            !OrderUtils.productIsAdditionalService(orderDetails.items, i.id)
    );

    const deliveryProducts = orderDetails.items.filter(
        (i) =>
            i.deliveryType === DeliveryType.HomeDelivery &&
            !OrderUtils.productIsAdditionalService(orderDetails.items, i.id)
    );

    const determineTranslationKeyForSupplierCount = () => {
        const supplierCount = orderDetails.supplierCount;
        const translationKey = t("orderOverview.text.multipleSuppliers");

        return supplierCount && supplierCount > 1 ? translationKey : "";
    };

    const clickAndCollectTime = () => {
        if (collectProducts && collectProducts.length > 0) {
            const result = collectProducts.filter(
                (product) =>
                    product.deliveryPromise &&
                    product.deliveryPromise.deliveryPromiseType ===
                        DeliveryPromiseType.AvailableForPickUpInStore
            );

            if (
                result.length > 0 &&
                result[0].deliveryPromise &&
                result[0].deliveryPromise.deliveryTime &&
                result[0].deliveryPromise.deliveryTimeUnit
            ) {
                return t(
                    "product.deliveryPromise.clickAndCollect",
                    result[0].deliveryPromise.deliveryTime,
                    t(
                        `product.deliveryPromise.timeUnits.${result[0].deliveryPromise.deliveryTimeUnit}`
                    )
                );
            }
        }

        return "";
    };

    const customerObject = orderDetails.customer;
    const orderText = variableText(
        innerHTML,
        customerObject?.email,
        determineTranslationKeyForSupplierCount(),
        orderDetails?.orderId
    );

    const isClickAndCollect = collectProducts.length > 0;

    return (
        <BreakpointProvider>
            <DeviceFingerPrintingProvider>
                <div>
                    {orderDetails && (
                        <>
                            <div
                                dangerouslySetInnerHTML={{ __html: orderText }}
                            />
                            <div>
                                <ProductCollectionBlock
                                    collectProducts={collectProducts}
                                    deliveryProducts={deliveryProducts}
                                    additionalServiceTooltip={
                                        additionalServiceTooltip
                                    }
                                    productTitleLink={false}
                                    reviewsAllowed={false}
                                    DeliveryPromiseComponent={
                                        DeliveryPromiseComponent
                                    }
                                />
                                <Row className={cn("tw:mb-2 tw:justify-end")}>
                                    <div className="col-12">
                                        <PriceSummaryComponent
                                            orderDetails={orderDetails}
                                        />
                                    </div>
                                </Row>
                                <h3>{t("orderOverview.title.orderDetails")}</h3>
                                <hr />
                                <div
                                    className={cn(
                                        "tw:columns-1 tw:md:columns-2 tw:lg:columns-3"
                                    )}
                                >
                                    <div
                                        className={cn(
                                            "tw:break-inside-avoid tw:p-0"
                                        )}
                                    >
                                        <OrderDetails
                                            orderDetails={orderDetails}
                                        />
                                    </div>
                                    {orderDetails.deliveryAddress && (
                                        <div
                                            className={cn(
                                                "tw:break-inside-avoid tw:p-0"
                                            )}
                                        >
                                            <OrderDeliveryDetailsBlock
                                                address={
                                                    orderDetails.deliveryAddress
                                                }
                                            />
                                        </div>
                                    )}
                                    {isClickAndCollect && (
                                        <div
                                            className={cn(
                                                "tw:break-inside-avoid tw:p-0"
                                            )}
                                        >
                                            <OrderClickAndCollectDetailsBlock
                                                pickUpTime={clickAndCollectTime()}
                                                store={storeDetails}
                                            />
                                        </div>
                                    )}
                                    <div
                                        className={cn(
                                            "tw:break-inside-avoid tw:p-0"
                                        )}
                                    >
                                        <OrderCustomerDetailsBlock
                                            orderObject={orderDetails}
                                            customerObject={customerObject}
                                        />
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </DeviceFingerPrintingProvider>
        </BreakpointProvider>
    );
};

export { OrderOverviewComponent };
